import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import _ from 'lodash';
import { Address } from './Address';
import { Occupations } from '../static/Occupations';
import { Customer } from './Customer';
import PubSub from 'pubsub-js';
import { SubjectManager } from '../subjectManager/SubjectManager';
import { Person } from './Person';
import { Financials as GQLFinancials } from '@hit/hit-api-client';
import { Bankruptcy } from './Bankruptcy';
import { Product } from '../static/Product';

@supertypeClass({ toClient: true, toServer: true })
export class Financials extends Everything implements GQLFinancials {
  @property({ getType: () => Customer, persist: false })
  customer: Customer;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you employed',
  })
  employed: boolean;

  @property({
    rule: ['text', 'required'],
    values: ['employed', 'unemployed', 'homemaker', 'retired', 'student'],
    descriptions: {
      employed: 'Employed',
      unemployed: 'Unemployed',
      homemaker: 'Stay-at-home parent/Homemaker',
      retired: 'Retired',
      student: 'Student',
    },
    comment: 'What is your occupation',
  })
  employmentStatus: string;

  @property({
    comment: 'Details regarding the employment status if retired',
  })
  employmentStatusDetails: string;

  @property({
    comment: 'If employment status is student',
  })
  graduationDate: Date;

  @property({
    comment: 'If employment status is student',
  })
  educationLevel: string;

  @property({
    comment: 'If employment status is student',
  })
  fieldOfStudy: string;

  @property({ rule: ['required'], comment: 'Are you a homemaker' })
  isHomeMaker: string;

  // Misnomer. The answer to 'My spouse, partner or fiance currently has ____ in life insurance coverage'.
  @property({
    rule: ['currencyZero', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'How much total life insurance coverage does your spouse or partner or fiance have',
  })
  coverageExistingAmt: number;

  // Misnomer. The answer to 'I do not have a spouse, partner, or fiance'.
  @property()
  noCoverageExistingAmt: boolean = false;

  @property({
    comment: "Insured does not know partner's coverage amount",
  })
  unknownCoverageExistingAmt: boolean = false;

  @property({ rule: ['required', 'text'], length: 40, comment: 'Who is your current employer' })
  employerName: string;

  @property({ rule: ['required'], comment: 'Current employer address', getType: () => Address })
  employerAddress: Address;

  @property({
    rule: ['currencyZero', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'Annual household earned income',
  })
  annualIncome: number;

  @property({
    rule: ['currencyZero', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'Insured pre-tax income (not combined househould income)',
  })
  insuredIncome: number;

  @property({
    rule: ['currencyZero', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'If insured has a spouse, combined pre-tax income',
  })
  annualHouseholdIncome: number;

  @property({
    rule: ['currencyZero', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'Annual household unearned income',
  })
  otherIncome: number;

  @property({ rule: ['currency', 'required'], validate: 'isWithin(0, 10000000)', comment: 'Household total assets' })
  totalAssets: number;

  @property({
    rule: ['currency', 'required'],
    validate: 'isWithin(0, 10000000)',
    comment: 'Household total liabilities',
  })
  totalLiabilities: number;

  @property({
    rule: ['currencyZero', 'required'],
    comment: 'Household net worth',
  })
  netWorth: number;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'determines if the policy applier is subject to backup witholding; YES means he/she is not subject to',
  })
  isNotFATCAWitholding: boolean;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'acknowledgement of being a US Citizen or Resident alien for FATCA specifically',
  })
  isFATCAResidencyStatus: boolean;

  @property({
    rule: ['text', 'required'],
    length: 40,
    values: function () {
      return _.map(Occupations.occupationList, (elem) => {
        return {
          listValue: elem.en,
          fieldValue: elem.en,
        };
      });
    },
    comment: 'What is your occupation',
  })
  occupation: string;

  @property({ rule: ['text', 'required'], length: 40 })
  occupationTranslated: string;

  @property({ rule: ['text'], length: 40 })
  occupationCode: string;

  @property({ rule: ['required', 'text'], comment: 'List your major job duties' })
  duties: string;

  @property({
    rule: ['numericPositive'],
    validate: 'isWithin(1,100)',
    comment: 'How long has the insured been unemployed?',
  })
  unemploymentDuration: number;

  @property({
    rule: ['numericPositive'],
    validate: 'isWithin(1,100)',
    comment: 'How long has the insured been unemployed? (In Months)',
  })
  unemploymentDurationMonths: number;

  @property({
    rule: ['text', 'required'],
    length: 40,
    values: function () {
      return _.map(Occupations.occupationList, (elem) => {
        return {
          listValue: elem.en,
          fieldValue: elem.en,
        };
      });
    },
    comment: "What was the insured's prior occupation?",
  })
  priorOccupation: string;

  @property({
    rule: ['currencyZero', 'numericPositive'],
    comment: "What was the insured's prior income?",
  })
  priorIncome: number;

  @property({
    values: ['0', '1', '2', '3', '4'],
    descriptions: {
      '0': 'Within 3 months',
      '1': 'Within 6 months',
      '2': 'Within 1 year',
      '3': 'Will not return to employment',
      '4': 'Not sure',
    },
    comment: 'When does the insured expect to return to employment?',
  })
  reemploymentExpectancy: string;

  @property({
    comment: 'Details regarding when the insured expects to return to employment',
  })
  reemploymentExpectancyDetails: string;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Has the insured ever filed for bankruptcy?',
  })
  hasBankruptcyHistory: string;

  @property({
    values: {
      ch7: 'Chapter 7',
      ch9: 'Chapter 9',
      ch11: 'Chapter 11',
      ch12: 'Chapter 12',
      ch13: 'Chapter 13',
    },
    comment: 'Type of bankruptcy the insured filed for',
  })
  bankruptcyType: string;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: "Has the insured's bankruptcy been discharged?",
  })
  isBankruptcyDischarged: string;

  @property({
    isLocal: true,
    comment: 'Transient property to reflect the negation of isBankruptcyDischarged',
  })
  get isBankruptcyNotDischarged(): boolean {
    return this.isBankruptcyDischarged ? this.isBankruptcyDischarged === 'false' : null;
  }

  set isBankruptcyNotDischarged(value: boolean) {
    this.isBankruptcyDischarged = value ? 'false' : 'true';
  }

  @property({
    rule: ['required', 'pastDate'],
    comment: "When was the insured's bankruptcy discharged?",
  })
  bankruptcyDischargeDate: Date;

  @property({ isLocal: true })
  private _unemployed: boolean;

  @property({ isLocal: true })
  get unemployed(): boolean {
    if (typeof this.employed === 'boolean') {
      return !this.employed;
    } else {
      return null;
    }
  }

  set unemployed(value: boolean) {
    this.employed = !value;
  }

  constructor() {
    super();
  }

  static new(customer) {
    return new Financials().init(customer);
  }

  init(customer) {
    this.employerAddress = Address.new(customer);
    this.customer = customer;
    return this;
  }

  noCoverageExistingAmtTrigger() {
    if (!this.noCoverageExistingAmt) {
      this.coverageExistingAmt = null;
      // eslint-disable-next-line no-undef
      PubSub.publish('customer_application_primaryPolicy_insured_financials_coverageExistingAmt_changed', '');
      SubjectManager.publish('customer_application_primaryPolicy_insured_financials_coverageExistingAmt_changed', '');
    }
  }

  employedTrigger() {
    if (!this.employed) {
      this.employerName = this.occupation = this.occupationCode = this.duties = null;
      this.employerAddress.reset();
    }
  }

  getEmployedAnnualIncome() {
    return this.employed ? this.annualIncome : 0;
  }

  getUnEmployedAnnualIncome() {
    return !this.employed ? this.annualIncome : 0;
  }

  getEmployedOtherIncome() {
    return this.employed ? this.otherIncome : 0;
  }

  getUnEmployedOtherIncome() {
    return !this.employed ? this.otherIncome : 0;
  }

  getEmployedNetWorth() {
    return this.employed ? this.netWorth : 0;
  }

  getUnEmployedNetWorth() {
    return !this.employed ? this.netWorth : 0;
  }

  occupationTrigger() {
    const occupation = this.occupation;

    const applicationName = this.amorphic.config.nconf.get('APPLICATION');
    /**
     * In the scenario where the insured is >= 16.5 years old
     * and the user selects "Yes" for the "Is <insured's name> employed?" question,
     * Coverpath and MMDirect ask "What is your occupation?"
     *
     * It is possible that the user then changes the DOB so the insured is now < 16.5 years old
     * If that happens, we hide the employment question and it's reflexives (which includes the occupation question)
     *
     * When a question goes from visible to not visible, we have logic in base-form.component.ts ngOnDestroy function
     * to reset the property of the question to null and run the associated trigger
     *
     * When 'occupation' here is set to null because the question is no longer visible and this trigger is ran,
     * we are setting employed = false, but that question is also not visible when insured < 16.5 years old so should remain null
     *
     * Coverpath/MMDirect never ask the occupation question unless the user selects "Yes" for the "Is <insured's name> employed?" question
     * so there is no need to ever set employed in this trigger for Coverpath/MMDirect
     */
    if (applicationName && !applicationName.includes('coverpath') && applicationName !== 'mm-direct') {
      this.employed = !!occupation;
    }

    const matchedOccupation: any = _.find(Occupations.occupationList, (o) => {
      return o.en === occupation;
    });

    if (matchedOccupation) {
      this.occupation = matchedOccupation.en;
      this.occupationTranslated = null;
      this.occupationCode = matchedOccupation.key;
    } else {
      this.occupation = occupation;
      this.occupationTranslated = null;
      this.occupationCode = null;
    }
  }

  occupationTranslatedMapped() {
    return this.occupationTranslated ? this.occupationTranslated : this.occupation;
  }

  unemployedTrigger() {
    if (this.unemployed) {
      this.employerName = this.occupation = this.occupationCode = this.duties = null;
      // eslint-disable-next-line no-undef
      PubSub.publish('customer_application_primaryPolicy_insured_financials_occupation_changed', '');
      SubjectManager.publish('customer_application_primaryPolicy_insured_financials_occupation_changed', '');
      this.employerAddress.reset();
    }
  }

  bankruptcyDischargeDateTrigger() {
    if (this.bankruptcyDischargeDate) {
      this.isBankruptcyNotDischarged = false;
    }
  }

  @property({ type: Array, getType: () => Bankruptcy })
  bankruptcies: Array<Bankruptcy> = [];
}
